// ---------------------------------------------------------------------------
// Numbers round: the step list, its validator, an expression parser, and the
// exhaustive solver.
//
// The step list is the canonical model. A step consumes two operands, each
// either a tile referenced BY INDEX or the result of an earlier step referenced
// by id, and produces one new value. Referencing tiles by index is what keeps
// two tiles showing 5 distinguishable and makes consumption trackable.
//
// Nothing in the validator may reveal anything about the target. Legality is
// not correctness: an illegal step is blocked, a legal-but-useless step is not.
// ---------------------------------------------------------------------------
import { NUMBERS_MAX_STEPS } from './constants.js';
/** 'A' through 'Z'. Six tiles can never need more than five, but be generous. */
const STEP_ID_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const DEFAULT_TIME_BUDGET_MS = 2_000;
// --- Operands ---------------------------------------------------------------
/** Resolve an operand against the tiles and the steps built so far. */
export function resolveOperand(operand, tiles, steps) {
    if (operand.kind === 'tile') {
        const index = operand.tileIndex;
        if (!Number.isInteger(index) || index < 0 || index >= tiles.length)
            return null;
        return tiles[index];
    }
    for (const step of steps) {
        if (step.id === operand.stepId)
            return step.result;
    }
    return null;
}
function sameOperand(a, b) {
    if (a.kind === 'tile' && b.kind === 'tile')
        return a.tileIndex === b.tileIndex;
    if (a.kind === 'step' && b.kind === 'step')
        return a.stepId === b.stepId;
    return false;
}
/** Operand ids consumed by the given steps, so the UI can grey out used tiles. */
export function consumedOperands(steps) {
    const tileIndices = new Set();
    const stepIds = new Set();
    for (const step of steps) {
        for (const operand of [step.left, step.right]) {
            if (operand.kind === 'tile')
                tileIndices.add(operand.tileIndex);
            else
                stepIds.add(operand.stepId);
        }
    }
    return { tileIndices, stepIds };
}
function isConsumed(operand, consumed) {
    return operand.kind === 'tile'
        ? consumed.tileIndices.has(operand.tileIndex)
        : consumed.stepIds.has(operand.stepId);
}
/** 'A', 'B', ... 'Z'. Next unused id for a step list. */
export function nextStepId(steps) {
    const used = new Set();
    for (const step of steps)
        used.add(step.id);
    for (const letter of STEP_ID_ALPHABET) {
        if (!used.has(letter))
            return letter;
    }
    return `S${steps.length + 1}`;
}
// --- Validation -------------------------------------------------------------
function applyOperator(left, right, op) {
    if (op === '/') {
        if (right === 0)
            return { ok: false, error: 'division-by-zero' };
        if (left % right !== 0)
            return { ok: false, error: 'non-integer-division' };
    }
    const result = op === '+' ? left + right : op === '-' ? left - right : op === '*' ? left * right : left / right;
    if (!Number.isInteger(result))
        return { ok: false, error: 'non-integer-division' };
    if (result <= 0)
        return { ok: false, error: 'non-positive-result' };
    return { ok: true, result };
}
/**
 * Validate one candidate step against the tiles and prior steps.
 *
 * Rejects, and only rejects, illegal moves: non-integer division, division by
 * zero, a non-positive result, reusing a consumed operand, an unknown operand,
 * exceeding NUMBERS_MAX_STEPS. Legality is not correctness. Nothing here may
 * reveal anything about the target.
 */
export function validateStep(tiles, steps, candidate) {
    if (steps.length >= NUMBERS_MAX_STEPS)
        return { ok: false, error: 'too-many-steps' };
    const left = resolveOperand(candidate.left, tiles, steps);
    const right = resolveOperand(candidate.right, tiles, steps);
    if (left === null || right === null)
        return { ok: false, error: 'unknown-operand' };
    const consumed = consumedOperands(steps);
    if (isConsumed(candidate.left, consumed) || isConsumed(candidate.right, consumed)) {
        return { ok: false, error: 'operand-already-consumed' };
    }
    if (sameOperand(candidate.left, candidate.right)) {
        return { ok: false, error: 'operand-already-consumed' };
    }
    return applyOperator(left, right, candidate.op);
}
/**
 * Validate a whole step list in order. Returns the index of the first bad step.
 *
 * Each step's declared `result` is ignored and recomputed, so a client that
 * lies about a result cannot inflate its answer. An empty step list is legal
 * and reports a value of 0, which no real step can ever produce, so callers can
 * treat 0 as "no answer".
 */
export function validateStepList(tiles, steps) {
    const built = [];
    for (let i = 0; i < steps.length; i += 1) {
        const step = steps[i];
        const check = validateStep(tiles, built, { left: step.left, right: step.right, op: step.op });
        if (!check.ok)
            return { ok: false, error: check.error, stepIndex: i };
        built.push({
            id: step.id,
            left: step.left,
            right: step.right,
            op: step.op,
            result: check.result,
        });
    }
    if (built.length === 0)
        return { ok: true, value: 0 };
    return { ok: true, value: built[built.length - 1].result };
}
// --- The best value reached -------------------------------------------------
//
// DESIGN §6.4, amended 2026-08-03. A numbers round scores the best value the
// player REACHED, not the one that happened to be on the board when the clock
// stopped, so something has to decide which of two values is the better answer
// and something has to notice every value a step list passes through. Both live
// here, pure and target-aware, because the engine calls them on the
// `applyAction` path and the play screen calls them on every keystroke, and two
// implementations of "which of these is better" would disagree on a Tuesday.
/**
 * Does `candidate` beat the best already held?
 *
 * Closest to the target wins. On a tie, the one reached in fewer steps. On a
 * tie there too, the one held — which is always the EARLIER one, because the
 * only way to become held is to have arrived first and nothing ever displaces a
 * held best except a strict improvement. That is the whole implementation of
 * "and after that the earlier one": there is no timestamp to compare.
 */
export function beatsNumbersBest(held, candidate) {
    if (held === null)
        return true;
    if (candidate.distance !== held.distance)
        return candidate.distance < held.distance;
    return candidate.steps.length < held.steps.length;
}
/**
 * Fold every value a step list passes through into the best held so far.
 *
 * Prefix by prefix, and that is the point rather than an optimisation: a step
 * list's declared answer is only its LAST step's result, so a player who
 * reached the target on step B and then built C has the target sitting in their
 * own working with nothing looking at it. Each prefix is a step list the player
 * could have stopped at and submitted, so each one's value counts as reached.
 *
 * Each step is re-validated as it is walked, exactly as `validateStepList`
 * does, so a client that lies about a `result` cannot bank a value it never
 * made — and an illegal step stops the walk rather than failing the whole call,
 * because the legal prefix in front of it genuinely did happen.
 *
 * Returns `held` itself, by identity, when nothing beat it. Callers use that to
 * avoid rewriting state that has not changed.
 */
export function foldNumbersBest(held, tiles, steps, target) {
    let best = held;
    const built = [];
    for (const step of steps) {
        const check = validateStep(tiles, built, { left: step.left, right: step.right, op: step.op });
        if (!check.ok)
            break;
        built.push({ id: step.id, left: step.left, right: step.right, op: step.op, result: check.result });
        const candidate = {
            value: check.result,
            distance: Math.abs(check.result - target),
            // Copied, because `built` keeps growing under it.
            steps: built.map((committed) => ({ ...committed })),
        };
        if (beatsNumbersBest(best, candidate))
            best = candidate;
    }
    return best;
}
/**
 * A step list read as a best, or null when it is not an answer at all.
 *
 * The board's own claim, for comparing against a banked best. An empty or
 * illegal list is not a claim: `validateStepList` reports an empty one as 0,
 * which no real step can produce.
 */
export function numbersBestOfSteps(tiles, steps, target) {
    if (steps.length === 0)
        return null;
    const checked = validateStepList(tiles, steps);
    if (!checked.ok)
        return null;
    return {
        value: checked.value,
        distance: Math.abs(checked.value - target),
        steps: steps.map((step) => ({ ...step })),
    };
}
/**
 * Every character a player might plausibly type for each operator, including
 * the pretty forms a phone keyboard offers.
 */
const OPERATOR_ALIASES = {
    '+': '+',
    '-': '-',
    '−': '-', // minus sign
    '–': '-', // en dash
    '*': '*',
    x: '*',
    X: '*',
    '×': '*', // multiplication sign
    '/': '/',
    '÷': '/', // division sign
};
function isDigit(ch) {
    return ch >= '0' && ch <= '9';
}
function tokenize(expression) {
    const tokens = [];
    let i = 0;
    while (i < expression.length) {
        const ch = expression.charAt(i);
        if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r') {
            i += 1;
            continue;
        }
        if (isDigit(ch)) {
            let j = i;
            while (j < expression.length && isDigit(expression.charAt(j)))
                j += 1;
            const value = Number(expression.slice(i, j));
            if (!Number.isSafeInteger(value))
                return null;
            tokens.push({ kind: 'number', value });
            i = j;
            continue;
        }
        if (ch === '(' || ch === '[') {
            tokens.push({ kind: 'lparen' });
            i += 1;
            continue;
        }
        if (ch === ')' || ch === ']') {
            tokens.push({ kind: 'rparen' });
            i += 1;
            continue;
        }
        const op = OPERATOR_ALIASES[ch];
        if (op !== undefined) {
            tokens.push({ kind: 'op', op });
            i += 1;
            continue;
        }
        return null;
    }
    return tokens;
}
const STEP_ERROR_MESSAGES = {
    'non-integer-division': 'That division does not come out whole.',
    'division-by-zero': 'You cannot divide by zero.',
    'non-positive-result': 'Every result has to be a positive whole number.',
    'operand-already-consumed': 'That tile has already been used.',
    'unknown-operand': 'That is not one of your tiles.',
    'too-many-steps': `You cannot use more than ${NUMBERS_MAX_STEPS} steps.`,
};
/**
 * Parse `((75+25)*3)+7` into the canonical step list.
 *
 * Standard precedence and parentheses. Each literal must match an unconsumed
 * tile. Returns the same errors as validateStep, plus 'unknown-operand' when a
 * literal is not among the tiles.
 */
export function parseExpression(tiles, expression) {
    const tokens = tokenize(expression);
    if (tokens === null) {
        return {
            ok: false,
            error: 'parse-error',
            message: 'That expression contains something I cannot read.',
        };
    }
    if (tokens.length === 0) {
        return { ok: false, error: 'parse-error', message: 'Type an expression first.' };
    }
    const steps = [];
    /** Tiles claimed by a literal, which may not yet belong to a step. */
    const claimedTiles = new Set();
    let pos = 0;
    const peek = () => (pos < tokens.length ? tokens[pos] : null);
    const fail = (error, message) => ({
        ok: false,
        error,
        message,
    });
    const takeLiteral = (value) => {
        for (let i = 0; i < tiles.length; i += 1) {
            if (tiles[i] === value && !claimedTiles.has(i)) {
                claimedTiles.add(i);
                return { ok: true, operand: { kind: 'tile', tileIndex: i } };
            }
        }
        if (tiles.includes(value)) {
            return fail('operand-already-consumed', `You have already used every ${value}.`);
        }
        return fail('unknown-operand', `${value} is not one of your tiles.`);
    };
    const applyBinary = (left, right, op) => {
        const check = validateStep(tiles, steps, { left, right, op });
        if (!check.ok)
            return fail(check.error, STEP_ERROR_MESSAGES[check.error]);
        const step = { id: nextStepId(steps), left, right, op, result: check.result };
        steps.push(step);
        return { ok: true, operand: { kind: 'step', stepId: step.id } };
    };
    const parseFactor = () => {
        const token = peek();
        if (token === null)
            return fail('parse-error', 'The expression stops too early.');
        if (token.kind === 'number') {
            pos += 1;
            return takeLiteral(token.value);
        }
        if (token.kind === 'lparen') {
            pos += 1;
            const inner = parseSum();
            if (!inner.ok)
                return inner;
            const closing = peek();
            if (closing === null || closing.kind !== 'rparen') {
                return fail('parse-error', 'A bracket is left open.');
            }
            pos += 1;
            return inner;
        }
        return fail('parse-error', 'Expected a number or an opening bracket.');
    };
    const parseTerm = () => {
        const first = parseFactor();
        if (!first.ok)
            return first;
        let accumulator = first.operand;
        for (;;) {
            const token = peek();
            if (token === null || token.kind !== 'op' || (token.op !== '*' && token.op !== '/')) {
                return { ok: true, operand: accumulator };
            }
            pos += 1;
            const right = parseFactor();
            if (!right.ok)
                return right;
            const combined = applyBinary(accumulator, right.operand, token.op);
            if (!combined.ok)
                return combined;
            accumulator = combined.operand;
        }
    };
    function parseSum() {
        const first = parseTerm();
        if (!first.ok)
            return first;
        let accumulator = first.operand;
        for (;;) {
            const token = peek();
            if (token === null || token.kind !== 'op' || (token.op !== '+' && token.op !== '-')) {
                return { ok: true, operand: accumulator };
            }
            pos += 1;
            const right = parseTerm();
            if (!right.ok)
                return right;
            const combined = applyBinary(accumulator, right.operand, token.op);
            if (!combined.ok)
                return combined;
            accumulator = combined.operand;
        }
    }
    const root = parseSum();
    if (!root.ok)
        return root;
    if (pos < tokens.length) {
        return fail('parse-error', 'There is something extra after the end of the expression.');
    }
    const value = resolveOperand(root.operand, tiles, steps);
    if (value === null)
        return fail('unknown-operand', 'That is not one of your tiles.');
    return { ok: true, steps, value };
}
// --- Rendering --------------------------------------------------------------
function precedenceOf(op) {
    return op === '+' || op === '-' ? 1 : 2;
}
/**
 * Render a step list back to a single infix expression, for the reveal.
 *
 * The last step is the root. Brackets are added whenever dropping them could
 * change the step structure, so the output always parses back to the same
 * tree: a right-hand child of equal precedence keeps its brackets, because
 * `a - (b - c)` and `a * (b / c)` are not `a - b - c` and `a * b / c`.
 */
export function stepsToExpression(tiles, steps) {
    if (steps.length === 0)
        return '';
    const byId = new Map();
    for (const step of steps)
        byId.set(step.id, step);
    const render = (operand, parentPrecedence, rightSide) => {
        if (operand.kind === 'tile') {
            const index = operand.tileIndex;
            if (!Number.isInteger(index) || index < 0 || index >= tiles.length)
                return '?';
            return String(tiles[index]);
        }
        const step = byId.get(operand.stepId);
        if (step === undefined)
            return '?';
        const precedence = precedenceOf(step.op);
        const body = `${render(step.left, precedence, false)} ${step.op} ${render(step.right, precedence, true)}`;
        const needsBrackets = rightSide ? precedence <= parentPrecedence : precedence < parentPrecedence;
        return needsBrackets ? `(${body})` : body;
    };
    const root = steps[steps.length - 1];
    return render({ kind: 'step', stepId: root.id }, 0, false);
}
/**
 * Depth-first search over every legal step list, calling `visit` once for every
 * value it can reach along with the step list that reaches it.
 *
 * Pruning, none of which loses a reachable value:
 *  - Identical value multisets are explored once. What a pool can reach depends
 *    only on its values, and every path to a pool of size k has used exactly
 *    `tiles.length - k` steps, so the first witness is never longer.
 *  - `+` and `*` are commutative, so only one ordering is tried. For `-` and
 *    `/` only larger-op-smaller can satisfy the positive-integer rule, so that
 *    direction is the only one generated rather than generated and discarded.
 *  - Multiplying or dividing by 1 is skipped. It burns a tile and a step to
 *    reproduce a value already in the pool, and since tiles may be left unused
 *    the same values stay reachable with strictly fewer steps.
 *
 * Date.now() appears here and nowhere else in this module: the time budget is
 * an engineering guard, not part of game state.
 */
function searchSteps(tiles, timeBudgetMs, visit) {
    const startedAt = Date.now();
    const seenPools = new Set();
    let ticks = 0;
    let expired = false;
    const initial = [];
    for (let i = 0; i < tiles.length; i += 1) {
        const value = tiles[i];
        if (!Number.isInteger(value) || value <= 0)
            continue;
        initial.push({ value, operand: { kind: 'tile', tileIndex: i } });
        visit(value, []);
    }
    const recurse = (pool, steps) => {
        if (expired)
            return;
        if (pool.length < 2 || steps.length >= NUMBERS_MAX_STEPS)
            return;
        ticks += 1;
        if ((ticks & 0x3ff) === 0 && Date.now() - startedAt > timeBudgetMs) {
            expired = true;
            return;
        }
        const key = pool
            .map((node) => node.value)
            .sort((a, b) => a - b)
            .join(',');
        if (seenPools.has(key))
            return;
        seenPools.add(key);
        const id = STEP_ID_ALPHABET.charAt(steps.length);
        for (let i = 0; i < pool.length; i += 1) {
            for (let j = i + 1; j < pool.length; j += 1) {
                const a = pool[i];
                const b = pool[j];
                const high = a.value >= b.value ? a : b;
                const low = a.value >= b.value ? b : a;
                const rest = [];
                for (let k = 0; k < pool.length; k += 1) {
                    if (k !== i && k !== j)
                        rest.push(pool[k]);
                }
                const emit = (left, right, op, result) => {
                    const step = {
                        id,
                        left: left.operand,
                        right: right.operand,
                        op,
                        result,
                    };
                    const nextSteps = [...steps, step];
                    visit(result, nextSteps);
                    recurse([...rest, { value: result, operand: { kind: 'step', stepId: id } }], nextSteps);
                };
                emit(a, b, '+', a.value + b.value);
                if (low.value !== 1)
                    emit(high, low, '*', high.value * low.value);
                if (high.value > low.value)
                    emit(high, low, '-', high.value - low.value);
                if (low.value > 1 && high.value % low.value === 0) {
                    emit(high, low, '/', high.value / low.value);
                }
                if (expired)
                    return;
            }
        }
    };
    recurse(initial, []);
}
/**
 * Exhaustive search over the six tiles.
 *
 * `best` is the closest solution found, preferring exact, then smaller
 * distance, then fewer steps. `solutions` holds distinct exact solutions,
 * fewest steps first, capped at `maxSolutions`; it is empty when nothing hits
 * the target exactly, in which case `best` still holds the nearest miss.
 */
export function solveNumbers(tiles, target, options) {
    const maxSolutions = Math.max(1, Math.floor(options?.maxSolutions ?? 10));
    const timeBudgetMs = options?.timeBudgetMs ?? DEFAULT_TIME_BUDGET_MS;
    let best = null;
    const exact = [];
    const exactKeys = new Set();
    searchSteps(tiles, timeBudgetMs, (value, steps) => {
        const distance = Math.abs(value - target);
        if (best === null ||
            distance < best.distance ||
            (distance === best.distance && steps.length < best.steps.length)) {
            best = { steps, value, distance };
        }
        if (distance !== 0)
            return;
        const worst = exact.length === 0 ? Infinity : exact[exact.length - 1].steps.length;
        if (exact.length >= maxSolutions && steps.length >= worst)
            return;
        const key = stepsToExpression(tiles, steps);
        if (exactKeys.has(key))
            return;
        exactKeys.add(key);
        exact.push({ steps, value, distance });
        exact.sort((x, y) => x.steps.length - y.steps.length);
        if (exact.length > maxSolutions)
            exact.pop();
    });
    return { best, solutions: exact };
}
/**
 * Every reachable value with a witness step list. Used for bot answer ranking.
 *
 * Deduplicated by value, keeping the shortest witness, and ordered nearest to
 * the target first so a bot can sample a percentile straight off the list.
 */
export function enumerateReachable(tiles, target, options) {
    const timeBudgetMs = options?.timeBudgetMs ?? DEFAULT_TIME_BUDGET_MS;
    const byValue = new Map();
    searchSteps(tiles, timeBudgetMs, (value, steps) => {
        const existing = byValue.get(value);
        if (existing === undefined || steps.length < existing.steps.length) {
            byValue.set(value, { steps, value, distance: Math.abs(value - target) });
        }
    });
    return [...byValue.values()].sort((a, b) => a.distance - b.distance || a.steps.length - b.steps.length || a.value - b.value);
}
